/**
 * CallDiagnostics (#702 + #703) — per-turn diagnostics panel for an
 * Alpha caller's conversation. Lives inline on AdminConversationDetail.
 *
 * Companion to EngagementTimeline:
 * - EngagementTimeline shows WHAT happened during the call (proposals,
 *   responses, empty turns) — the product narrative.
 * - CallDiagnostics shows HOW the voice pipeline performed — STT
 *   confidence, latencies, errors per turn, plus the tablet's device
 *   snapshot and client-side events (#703). Used when an Alpha tester
 *   reports "she didn't understand me" / "her voice was weird" and
 *   we need to figure out where the breakdown was.
 *
 * #703 additions:
 * - Device panel renders the tablet's browser / OS / network / mic
 *   permission / audio device / app version captured at call start.
 * - Client-events timeline renders reconnect / AudioContext-suspend
 *   / uncaught-error events the tablet phoned home during the call.
 * - Per-turn inline <audio> player when the server captured TTS
 *   output bytes (TTS_RECORDING_DIR env var enabled).
 *
 * #924 — Turns grid switched from one-row-per-user-turn (post-#910)
 * to one-row-per-transcript-entry (interleaved chronological). Turn 1
 * = AI greeting, Turn 2 = elderly's first reply, Turn 3 = AI's reply,
 * ... matches the natural reading of a transcript. Rows are built
 * from `data.transcript` (the new `transcript_jsonb` field on the
 * diagnostics response); per-turn STT/LLM/TTS metrics from
 * `data.turns` merge onto the matching user-row by combined index
 * (user reply N at combined-idx 2N-1). Assistant rows show role
 * label "Berta" + content + (for the assistant reply right after a
 * user turn) the metrics from that user turn. DOM id contract:
 * `turn-${i+1}` where i is the 0-based combined transcript-jsonb
 * index. Click-to-scroll from CardsHandView's evidence chips lands
 * on the correct chronological row.
 *
 * Audio fetch goes through an authenticated blob fetch — the audio
 * URL inherits the admin auth requirement of the JSON endpoint, but
 * <audio> tags can't carry Bearer headers, so we fetch as a blob and
 * hand off via URL.createObjectURL.
 */
import React, { useEffect, useState, useRef } from 'react';
import { adminApi } from './adminApi';

function fmtMs(v) {
  if (v === null || v === undefined) return '—';
  return `${v} ms`;
}

function fmtConfidence(v) {
  if (v === null || v === undefined) return '—';
  return v.toFixed(2);
}

function fmtTokens(v) {
  if (v === null || v === undefined) return '—';
  return String(v);
}

function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  // #716 follow-up — display in UTC so admin diagnostic timestamps
  // match the server log lines directly (no timezone math to
  // correlate a captured event with `[audio_end #381]` etc.).
  // Server logs use UTC; tablet's captured `ts` fields are UTC ISO
  // strings; we render in UTC for an apples-to-apples comparison.
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  const ms = String(d.getUTCMilliseconds()).padStart(3, '0');
  return `${h}:${m}:${s}.${ms}Z`;
}

function fmtNet(downlink, rtt, effective) {
  const parts = [];
  if (effective) parts.push(effective);
  if (downlink !== null && downlink !== undefined) {
    parts.push(`${downlink.toFixed(1)} Mbps`);
  }
  if (rtt !== null && rtt !== undefined) parts.push(`rtt ${rtt}ms`);
  return parts.join(' · ') || '—';
}

// #703 — auth-aware audio fetch. Returns an object URL ready to drop
// into an <audio src>. The caller is responsible for revoking the URL
// when the player unmounts.
async function fetchAudioObjectUrl(url) {
  const token = localStorage.getItem('am_admin_token');
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Audio fetch failed (HTTP ${res.status})`);
  return URL.createObjectURL(await res.blob());
}

function TurnAudioPlayer({ url }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    fetchAudioObjectUrl(url)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u);
          return;
        }
        createdUrl = u;
        setBlobUrl(u);
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [url]);

  if (error) return <div style={styles.error}>Audio error: {error}</div>;
  if (!blobUrl) return <div style={styles.muted}>Loading audio…</div>;
  return (
    <audio
      controls
      src={blobUrl}
      style={{ width: '100%', maxWidth: 480 }}
    />
  );
}

// #924 — Build the interleaved chronological row list. Sources two
// inputs from the diagnostics response:
//   - `data.transcript`: [{role, content}, ...] in chronological order
//     (greeting at index 0). Source of truth for which rows to show,
//     in what order, with what content + speaker label.
//   - `data.turns`: [{turn_index, stt, llm, tts, total_turn_ms, error,
//     created_at, tts_audio_url}, ...] keyed on user-turn-1-based
//     `turn_index`. Merged onto the row at combined-0-based index
//     2*turn_index - 1 (the user reply N) AND the following assistant
//     reply at index 2*turn_index — the `turn_diagnostics` row spans
//     BOTH (user STT + Berta's LLM/TTS for the reply).
//
// Fallback shapes:
//   - When `data.transcript` is absent (legacy cache, very old call
//     pre-migration 0052), derive rows from `data.turns` alone:
//     each turn becomes a synthetic user row at idx 2N-1; no
//     assistant rows are rendered because content is unknown.
//   - When both are empty, returns []; the caller renders the
//     empty-state message.
//
// Exported for unit testing.
export function buildInterleavedRows(data) {
  if (!data) return [];
  const turnsByUserIdx = {};
  for (const t of data.turns || []) {
    if (Number.isFinite(t.turn_index)) {
      turnsByUserIdx[t.turn_index] = t;
    }
  }
  const transcript = Array.isArray(data.transcript) ? data.transcript : null;
  const rows = [];
  if (transcript && transcript.length > 0) {
    for (let i = 0; i < transcript.length; i++) {
      const entry = transcript[i];
      const role = entry?.role || 'assistant';
      const content = entry?.content || '';
      // For user reply at combined-0 idx i (i must be odd):
      //   user-turn N = (i + 1) / 2
      // For assistant reply at combined-0 idx i (i must be even, i>0):
      //   user-turn N = i / 2 (this assistant row is the reply that
      //   the same turn_diagnostics row produced).
      let diag = null;
      if (role === 'user' && i % 2 === 1) {
        diag = turnsByUserIdx[(i + 1) / 2] || null;
      } else if (role === 'assistant' && i > 0 && i % 2 === 0) {
        diag = turnsByUserIdx[i / 2] || null;
      }
      rows.push({
        combinedIdx: i,
        label: i + 1,
        role,
        content,
        diag,
      });
    }
    return rows;
  }
  // Fallback: no transcript, derive synthetic user-only rows.
  for (const t of data.turns || []) {
    const N = t.turn_index;
    if (!Number.isFinite(N)) continue;
    const combinedIdx = 2 * N - 1;
    rows.push({
      combinedIdx,
      label: combinedIdx + 1,
      role: 'user',
      content: t?.stt?.transcript || '',
      diag: t,
    });
  }
  return rows;
}


export default function CallDiagnostics({ convId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // #924 — `expanded` keyed on combined-1-based turn label (matches
  // the DOM-id contract). Pre-#924 it was `turn_diagnostics.turn_index`
  // (user-turn-1-based); after switching to interleaved rows the
  // label IS the canonical row identifier.
  const [expanded, setExpanded] = useState(null);
  // #839 — combined-0-based transcript-jsonb idx → [{ card_key,
  // display_label }] map built from the same `/admin/conversations/{id}/hand`
  // endpoint CardsHandView fetches. We do a parallel fetch rather
  // than lifting state up because the two components are otherwise
  // independent; one extra HTTP call is fine on an admin page.
  // The key matches `wildcard_segments.turn_start..turn_end` storage
  // (0-based combined indices); display labels are i+1 (#924).
  const [evidenceByTurn, setEvidenceByTurn] = useState({});

  useEffect(() => {
    if (!convId) return;
    adminApi
      .getConversationDiagnostics(convId)
      .then(setData)
      .catch((e) => setError(e.message));
  }, [convId]);

  // #839 — fetch hand, build turn-index → card-evidence map. Errors
  // are silent here on purpose: hand data is an enrichment, not the
  // primary content of this panel. Failure leaves badges empty.
  useEffect(() => {
    if (!convId) return;
    let cancelled = false;
    const token = localStorage.getItem('am_admin_token');
    fetch(`/api/admin/conversations/${convId}/hand`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => (r.ok ? r.json() : null))
      .then(payload => {
        if (cancelled || !payload || !Array.isArray(payload.hand)) return;
        const map = {};
        for (const card of payload.hand) {
          const idxs = Array.isArray(card.evidence_turn_indices)
            ? card.evidence_turn_indices : [];
          for (const idx of idxs) {
            if (!map[idx]) map[idx] = [];
            map[idx].push({
              card_key: card.card_key,
              display_label: card.display_label || card.card_key,
            });
          }
        }
        setEvidenceByTurn(map);
      })
      .catch(() => { /* silent — see comment above */ });
    return () => { cancelled = true; };
  }, [convId]);

  const cd = data?.client_diagnostics;
  const ccl = data?.client_console_logs;

  return (
    <div style={styles.container}>
      <div style={styles.title}>Call Diagnostics (Internal — #702 + #703)</div>

      {error && <div style={styles.error}>Error: {error}</div>}
      {data === null && !error && <div style={styles.muted}>Loading…</div>}

      {/* Device panel — #703 phone-home snapshot */}
      {cd && (
        <div style={styles.devicePanel}>
          <div style={styles.deviceTitle}>Device (tablet)</div>
          <div style={styles.deviceGrid}>
            <DeviceItem label="Browser" value={
              cd.device.browser
                ? `${cd.device.browser} ${cd.device.browser_version || ''}`.trim()
                : '—'
            } />
            <DeviceItem label="OS" value={
              cd.device.os
                ? `${cd.device.os} ${cd.device.os_version || ''}`.trim()
                : '—'
            } />
            <DeviceItem label="Device" value={cd.device.device_type || '—'} />
            <DeviceItem label="Viewport" value={
              cd.device.viewport_w && cd.device.viewport_h
                ? `${cd.device.viewport_w} × ${cd.device.viewport_h}`
                : '—'
            } />
            <DeviceItem label="Network" value={fmtNet(
              cd.device.network_downlink_mbps,
              cd.device.network_rtt_ms,
              cd.device.network_effective_type
            )} />
            <DeviceItem label="Mic permission" value={cd.device.mic_permission || '—'} />
            <DeviceItem label="App version" value={cd.device.app_version || '—'} />
          </div>
        </div>
      )}

      {/* Client events timeline — #703 phone-home events */}
      {cd && cd.events.length > 0 && (
        <div style={styles.eventsPanel}>
          <div style={styles.deviceTitle}>Client events</div>
          <div style={styles.eventsList}>
            {cd.events.map((evt, idx) => (
              <div key={idx} style={styles.eventRow}>
                <span style={styles.eventTs}>{fmtTime(evt.ts)}</span>
                <span style={styles.eventType}>{evt.type}</span>
                <span style={styles.eventPayload}>
                  {evt.payload && Object.keys(evt.payload).length > 0
                    ? JSON.stringify(evt.payload)
                    : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Client console log — #718 captured console.* + window.error */}
      {ccl && ccl.entries && ccl.entries.length > 0 && (
        <div style={styles.eventsPanel}>
          <div style={styles.deviceTitle}>
            Client console ({ccl.entry_count}
            {ccl.truncated ? ', truncated — older entries dropped' : ''})
          </div>
          <div style={styles.consoleList}>
            {ccl.entries.map((entry, idx) => (
              <div
                key={idx}
                style={{
                  ...styles.consoleRow,
                  ...(entry.level === 'error' ? styles.consoleError : {}),
                  ...(entry.level === 'warn' ? styles.consoleWarn : {}),
                }}
              >
                <span style={styles.consoleTs}>{fmtTime(entry.ts)}</span>
                <span style={styles.consoleLevel}>
                  {(entry.level || 'log').toUpperCase()}
                </span>
                <span style={styles.consoleMsg}>{entry.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && data.turns.length === 0 && (!data.transcript || data.transcript.length === 0) && (
        <div style={styles.muted}>
          No per-turn diagnostics for this call (predates the feature, or
          all turns were filtered out).
        </div>
      )}

      {data && (data.turns.length > 0 || (data.transcript && data.transcript.length > 0)) && (
        <>
          <div style={styles.summary}>
            <span style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Turns</span>
              <span style={styles.summaryValue}>{data.summary.turn_count}</span>
            </span>
            <span style={styles.summaryItem}>
              <span style={styles.summaryLabel}>Errors</span>
              <span
                style={{
                  ...styles.summaryValue,
                  color: data.summary.error_count > 0 ? '#C0392B' : '#27AE60',
                }}
              >
                {data.summary.error_count}
              </span>
            </span>
            <span style={styles.summaryItem}>
              <span style={styles.summaryLabel}>p50 total</span>
              <span style={styles.summaryValue}>{fmtMs(data.summary.p50_total_ms)}</span>
            </span>
            <span style={styles.summaryItem}>
              <span style={styles.summaryLabel}>p95 total</span>
              <span style={styles.summaryValue}>{fmtMs(data.summary.p95_total_ms)}</span>
            </span>
            {/* #985 — the audio-pipeline version this call ran on. Lets you
                tie a specific (good/bad) call back to its exact config. */}
            {data.summary.pipeline_version && (
              <span style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Pipeline</span>
                <span
                  style={styles.summaryValue}
                  title="Effective audio-pipeline version this call ran on (#985)"
                >
                  {data.summary.pipeline_version}
                </span>
              </span>
            )}
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th
                    style={styles.th}
                    title="Chronological 1-based turn label (#924). Turn 1 = AI greeting; Turn 2 = elderly's first reply; Turn 3 = AI's reply; etc. Storage stays 0-based; conversion is presentational."
                  >
                    Turn
                  </th>
                  <th
                    style={styles.th}
                    title="Who is speaking. Assistant rows show Berta's reply; user rows show the elderly user's STT transcript."
                  >
                    Speaker
                  </th>
                  <th
                    style={styles.th}
                    title="Transcript content for this row. User rows: Deepgram STT result. Assistant rows: Berta's text reply (verbatim transcript_jsonb)."
                  >
                    Content
                  </th>
                  <th
                    style={styles.th}
                    title="Wall-clock time the diagnostics row was written (≈ end of the user turn). Empty on assistant-greeting and assistant-reply rows (timing is captured per user turn)."
                  >
                    Time
                  </th>
                  <th
                    style={styles.th}
                    title="Deepgram STT confidence score for the final transcript (0–1). Higher = more certain. Low values often correlate with misheard transcripts. User rows only."
                  >
                    Confidence
                  </th>
                  <th
                    style={styles.th}
                    title="Time from audio_end (user stopped speaking) to Deepgram returning the final transcript. User rows only."
                  >
                    STT ms
                  </th>
                  <th
                    style={styles.th}
                    title="Anthropic input / output token counts for the LLM call that produced this assistant reply. Assistant rows (except greeting) only."
                  >
                    LLM tokens (in/out)
                  </th>
                  <th
                    style={styles.th}
                    title="Total LLM streaming time: from the moment voice.py dispatched chat_stream into the executor to the end-of-stream sentinel. Includes Anthropic TTFT + all sentences streamed back. Assistant rows (except greeting) only."
                  >
                    LLM ms
                  </th>
                  <th
                    style={styles.th}
                    title="Time from LLM dispatch to the FIRST TTS audio chunk being sent to the tablet over the WebSocket. Includes Anthropic TTFT + sentence segmentation + TTS provider TTFB. NOT the pure synthesis time. Assistant rows (except greeting) only."
                  >
                    TTS ms
                  </th>
                  <th
                    style={styles.th}
                    title="Perceived end-to-end response latency: from audio_end (user stopped speaking) to the first audio chunk of Berta's reply arriving on the WebSocket. Shown on the assistant-reply row that follows the user row. The number that matches what the user feels."
                  >
                    Total ms
                  </th>
                  <th
                    style={styles.th}
                    title="▶ when server-side TTS-output audio was captured for this assistant reply (TTS_RECORDING_DIR enabled). Click the row to expand and play."
                  >
                    Audio
                  </th>
                  <th
                    style={styles.th}
                    title="Stage at which the turn failed (stt / llm / tts) with the error code. Empty when the turn succeeded."
                  >
                    Error
                  </th>
                </tr>
              </thead>
              <tbody>
                {buildInterleavedRows(data).map((row) => {
                  const isOpen = expanded === row.label;
                  const rowStyle = (row.diag && row.diag.error)
                    ? { ...styles.tr, ...styles.trError }
                    : styles.tr;
                  // #839 — cards whose `evidence_turn_indices` includes
                  // this row's combined 0-based index. Rendered as small
                  // chips next to the Turn label so the admin can scan
                  // which turns were evidence for which card. The DOM
                  // `id="turn-${label}"` lets CardsHandView's evidence
                  // chips scroll to this row (DOM-id contract #924).
                  const cardBadges = evidenceByTurn[row.combinedIdx] || [];
                  const roleLabel = row.role === 'assistant' ? 'Berta' : 'User';
                  const speakerStyle = row.role === 'assistant'
                    ? styles.speakerAssistant
                    : styles.speakerUser;
                  const diag = row.diag;
                  // Per-row diagnostics: user rows show STT metrics;
                  // assistant-reply rows show LLM + TTS + total metrics
                  // + audio player (greeting has no diag — it predates
                  // any user turn).
                  const showSttMetrics = row.role === 'user' && diag != null;
                  const showLlmTtsMetrics = row.role === 'assistant' && diag != null;
                  // Content excerpt — keep table compact but legible.
                  const contentExcerpt = (row.content || '').trim();
                  const displayContent = contentExcerpt.length > 140
                    ? contentExcerpt.slice(0, 137) + '...'
                    : contentExcerpt;
                  return (
                    <React.Fragment key={`turn-${row.label}`}>
                      <tr
                        id={`turn-${row.label}`}
                        style={{ ...rowStyle, cursor: 'pointer' }}
                        onClick={() =>
                          setExpanded(isOpen ? null : row.label)
                        }
                      >
                        <td style={styles.td}>
                          #{row.label}
                          {cardBadges.length > 0 && (
                            <span style={{ marginLeft: 6, display: 'inline-flex', flexWrap: 'wrap', gap: 3, verticalAlign: 'middle' }}>
                              {cardBadges.map((b) => (
                                <span
                                  key={b.card_key}
                                  title={`Evidence for ${b.display_label}`}
                                  style={styles.cardBadge}
                                >
                                  {b.display_label}
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td style={{ ...styles.td, ...speakerStyle }}>
                          {roleLabel}
                        </td>
                        <td style={{ ...styles.td, ...styles.tdContent }}
                            title={contentExcerpt || undefined}>
                          {displayContent || <span style={styles.muted}>(empty)</span>}
                        </td>
                        <td style={styles.td}>
                          {diag ? fmtTime(diag.created_at) : '—'}
                        </td>
                        <td style={styles.td}>
                          {showSttMetrics ? fmtConfidence(diag.stt.confidence) : '—'}
                        </td>
                        <td style={styles.td}>
                          {showSttMetrics ? fmtMs(diag.stt.final_ms) : '—'}
                        </td>
                        <td style={styles.td}>
                          {showLlmTtsMetrics
                            ? `${fmtTokens(diag.llm.input_tokens)} / ${fmtTokens(diag.llm.output_tokens)}`
                            : '—'}
                        </td>
                        <td style={styles.td}>
                          {showLlmTtsMetrics ? fmtMs(diag.llm.completion_ms) : '—'}
                        </td>
                        <td style={styles.td}>
                          {showLlmTtsMetrics ? fmtMs(diag.tts.first_chunk_ms) : '—'}
                        </td>
                        <td style={styles.td}>
                          {showLlmTtsMetrics ? fmtMs(diag.total_turn_ms) : '—'}
                        </td>
                        <td style={styles.td}>
                          {showLlmTtsMetrics && diag.tts_audio_url ? '▶' : '—'}
                        </td>
                        <td style={styles.td}>
                          {diag && diag.error ? (
                            <span style={styles.errorStage}>
                              {diag.error.stage}
                              {diag.error.code ? ` · ${diag.error.code}` : ''}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={12} style={styles.expandedCell}>
                            {showLlmTtsMetrics && diag.tts_audio_url && (
                              <div style={styles.audioBlock}>
                                <div style={styles.audioLabel}>
                                  Server-captured TTS audio (what we sent)
                                </div>
                                <TurnAudioPlayer url={diag.tts_audio_url} />
                              </div>
                            )}
                            <pre style={styles.expandedPre}>
                              {JSON.stringify(
                                { row, diag },
                                null,
                                2,
                              )}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function DeviceItem({ label, value }) {
  return (
    <div style={styles.deviceItem}>
      <div style={styles.deviceItemLabel}>{label}</div>
      <div style={styles.deviceItemValue}>{value}</div>
    </div>
  );
}

const styles = {
  container: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    padding: '20px 24px',
    marginTop: 20,
  },
  title: {
    fontSize: '0.9rem',
    fontWeight: 700,
    color: '#2C2C2C',
    marginBottom: 14,
    letterSpacing: '-0.01em',
  },
  devicePanel: {
    background: '#FAF8F5',
    border: '1px solid #F0ECE6',
    borderRadius: 8,
    padding: '12px 14px',
    marginBottom: 14,
  },
  deviceTitle: {
    fontSize: '0.75rem',
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
  },
  deviceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 10,
  },
  deviceItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  deviceItemLabel: {
    fontSize: '0.7rem',
    color: '#aaa',
    fontWeight: 500,
  },
  deviceItemValue: {
    fontSize: '0.82rem',
    color: '#2C2C2C',
    fontWeight: 500,
  },
  eventsPanel: {
    marginBottom: 14,
  },
  eventsList: { display: 'flex', flexDirection: 'column', gap: 4 },
  eventRow: {
    display: 'grid',
    gridTemplateColumns: '90px 180px 1fr',
    alignItems: 'baseline',
    fontSize: '0.8rem',
    gap: 8,
  },
  eventTs: { color: '#888', fontFamily: 'Menlo, monospace', fontSize: '0.75rem' },
  eventType: { color: '#2C2C2C', fontWeight: 500 },
  eventPayload: { color: '#777', fontSize: '0.75rem', fontFamily: 'Menlo, monospace' },
  // #718 — console-log entries are a denser stream; tighter rows,
  // monospace throughout. Level coloring matches dev-console
  // convention (warn = amber, error = red).
  consoleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    background: '#FAF8F5',
    border: '1px solid #F0ECE6',
    borderRadius: 8,
    padding: '8px 10px',
    maxHeight: 360,
    overflowY: 'auto',
  },
  consoleRow: {
    display: 'grid',
    gridTemplateColumns: '70px 50px 1fr',
    alignItems: 'baseline',
    gap: 6,
    fontFamily: 'Menlo, monospace',
    fontSize: '0.72rem',
    color: '#444',
    padding: '1px 0',
    borderBottom: '1px dashed transparent',
  },
  consoleError: { color: '#C0392B' },
  consoleWarn: { color: '#B8860B' },
  consoleTs: { color: '#aaa', fontSize: '0.68rem' },
  consoleLevel: {
    color: '#aaa',
    fontSize: '0.65rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  consoleMsg: { whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  summary: {
    display: 'flex',
    gap: 24,
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  summaryItem: { display: 'flex', flexDirection: 'column', gap: 2 },
  summaryLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  summaryValue: { fontSize: '1rem', fontWeight: 600, color: '#2C2C2C' },
  tableWrap: { overflowX: 'auto' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.82rem',
  },
  th: {
    textAlign: 'left',
    padding: '6px 8px',
    color: '#888',
    fontWeight: 600,
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '1px solid #eee',
    whiteSpace: 'nowrap',
    // #702 follow-up — `cursor: help` signals to the admin that
    // every column header has a tooltip explaining what its value
    // actually measures (browsers render the native `title` attr
    // on hover).
    cursor: 'help',
  },
  tr: { borderBottom: '1px dashed #f0ece6' },
  trError: { background: '#FFF6F4' },
  td: {
    padding: '8px',
    color: '#2C2C2C',
    fontFamily: 'Menlo, monospace',
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
  },
  // #924 — Content cell may carry sentence-long transcript text;
  // allow wrapping and cap width so the row stays readable without
  // the dense-metrics columns being squashed.
  tdContent: {
    whiteSpace: 'normal',
    fontFamily: 'system-ui, sans-serif',
    fontSize: '0.82rem',
    maxWidth: 360,
    minWidth: 220,
    color: '#2C2C2C',
    lineHeight: 1.35,
  },
  // #924 — Speaker label colour distinguishes assistant ("Berta") vs
  // user rows at a glance. Coral matches the brand assistant accent.
  speakerAssistant: {
    color: '#D87151',
    fontWeight: 600,
    fontFamily: 'system-ui, sans-serif',
  },
  speakerUser: {
    color: '#2C3E50',
    fontWeight: 600,
    fontFamily: 'system-ui, sans-serif',
  },
  errorStage: { color: '#C0392B', fontWeight: 600 },
  // #839 — small chip next to the Turn # cell, marking that this
  // turn was evidence for the named card. Multiple chips render
  // inline when several cards share evidence on the same turn.
  cardBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 8,
    fontSize: '0.62rem',
    fontWeight: 600,
    background: '#E8F5E9',
    color: '#2E7D32',
    border: '1px solid #C8E6C9',
    fontFamily: 'ui-monospace, monospace',
    whiteSpace: 'nowrap',
  },
  expandedCell: { padding: '8px 8px 16px 8px', background: '#FAF8F5' },
  expandedPre: {
    margin: 0,
    fontSize: '0.75rem',
    color: '#555',
    fontFamily: 'Menlo, monospace',
    overflowX: 'auto',
  },
  audioBlock: { marginBottom: 12 },
  audioLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  error: { color: '#C0392B', fontSize: '0.85rem' },
  muted: { color: '#aaa', fontSize: '0.85rem', fontStyle: 'italic' },
};
